import { useEffect, useState } from "react";
import { useLanguage } from "@/i18n/LanguageContext";

interface TOCItem {
  id: string;
  text: string;
  level: number;
}

interface ArticleTableOfContentsProps {
  contentHtml: string;
}

const ArticleTableOfContents = ({ contentHtml }: ArticleTableOfContentsProps) => {
  const { lang } = useLanguage();
  const [headings, setHeadings] = useState<TOCItem[]>([]);
  const [activeId, setActiveId] = useState<string>("");

  useEffect(() => {
    // Parse headings from HTML content
    const parser = new DOMParser();
    const doc = parser.parseFromString(contentHtml, "text/html");
    const h2s = doc.querySelectorAll("h2, h3");
    const items: TOCItem[] = [];

    h2s.forEach((h, i) => {
      const id = `heading-${i}`;
      const text = h.textContent || "";
      const level = h.tagName === "H2" ? 2 : 3;
      if (text.trim()) items.push({ id, text, level });
    });

    setHeadings(items);
  }, [contentHtml]);

  // Observe scroll position for active heading
  useEffect(() => {
    if (headings.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          setActiveId(visible[0]!.target.id);
        }
      },
      { rootMargin: "-80px 0px -60% 0px", threshold: 0 }
    );

    headings.forEach((h) => {
      const el = document.getElementById(h.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [headings]);

  if (headings.length < 2) return null;

  return (
    <nav
      aria-label={lang === "es" ? "Índice de contenidos" : "Table of contents"}
      className="rounded-xl border border-border bg-muted/30 p-5 space-y-3"
    >
      <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
        {lang === "es" ? "En este artículo" : "In this article"}
      </p>
      <ol className="space-y-1.5 list-none m-0 p-0">
        {headings.map((h) => (
          <li key={h.id} className={h.level === 3 ? "pl-4" : ""}>
            <a
              href={`#${h.id}`}
              onClick={(e) => {
                e.preventDefault();
                document.getElementById(h.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className={`block text-sm leading-snug py-0.5 transition-colors hover:text-foreground ${
                activeId === h.id
                  ? "text-foreground font-semibold"
                  : "text-muted-foreground"
              }`}
            >
              {h.text}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
};

export default ArticleTableOfContents;
